import { escapeHtml } from '../ui.js'
import { apiConfigured, clientLogout, clientPendingSignatures, fetchClientSigningPdf } from '../api.js'
import {
  clearClientSession,
  getClientSession,
  hasPermission,
  isClientLoggedIn,
} from '../clientAuth.js'
import {
  persistPortalActiveInstallationId,
  resolvePortalActiveInstallationId,
} from '../portalInstallContext.js'

const DOCS_PATH = '/mi-instalacion/documentos'
const LEGACY_PATH = '/mi-instalacion/documentos-pendientes'

export function renderPendingDocs() {
  return `
    <div class="dash-app">
      <aside class="dash-side">
        <p class="dash-side-label">Portal</p>
        <nav class="dash-side-nav" aria-label="Portal cliente">
          <a href="/mi-instalacion" data-link>Resumen</a>
          <a href="${DOCS_PATH}" data-link class="is-active" aria-current="page">Documentos</a>
          <a href="/mi-instalacion/nueva-incidencia" data-link>Nueva incidencia</a>
          <a href="/mi-instalacion/presupuesto-it" data-link>Solicitar presupuesto</a>
          <a href="/mi-instalacion/gemelo" data-link id="dash-twin-btn" hidden>Gemelo 3D</a>
          <a href="/ia" data-link>Asistente IA</a>
        </nav>
        <button type="button" id="pending-logout" class="portal-btn dash-side-logout">Salir</button>
      </aside>
      <div class="dash-main">
        <div class="dash-hero">
          <div>
            <p class="portal-eyebrow">Firma documental</p>
            <h1>Mis documentos</h1>
            <p class="portal-muted" id="pending-subtitle">Contratos y mandatos SEPA de su instalación.</p>
          </div>
          <div class="dash-hero-actions">
            <a href="/mi-instalacion" data-link class="portal-btn portal-btn-rounded">Volver al resumen</a>
          </div>
        </div>
        <div id="pending-list" class="portal-card" style="padding:0;overflow:hidden;"></div>
        <div id="pending-pdf-viewer" class="portal-pdf-viewer" hidden data-testid="portal-docs-pdf-viewer">
          <div class="portal-pdf-viewer__toolbar">
            <div class="portal-pdf-viewer__title" id="pending-pdf-title">Documento PDF</div>
            <div class="portal-pdf-viewer__tools" role="toolbar" aria-label="Herramientas PDF">
              <button type="button" class="portal-pdf-tool" data-pdf-zoom="-1" title="Alejar" aria-label="Alejar">−</button>
              <span class="portal-pdf-viewer__zoom-label" data-pdf-zoom-label>100%</span>
              <button type="button" class="portal-pdf-tool" data-pdf-zoom="1" title="Acercar" aria-label="Acercar">+</button>
              <button type="button" class="portal-pdf-tool" data-pdf-fullscreen title="Pantalla completa" aria-label="Pantalla completa">⛶</button>
              <a class="portal-pdf-tool portal-pdf-tool--dl" id="pending-pdf-dl-link" href="#" download title="Descargar PDF">Descargar</a>
              <button type="button" class="portal-pdf-tool portal-pdf-tool--close" data-pdf-close title="Cerrar" aria-label="Cerrar">×</button>
            </div>
          </div>
          <div class="portal-pdf-viewer__frame-wrap">
            <iframe class="portal-pdf-viewer__frame" id="pending-pdf-frame" title="Vista previa PDF"></iframe>
          </div>
        </div>
        <p id="pending-msg" class="portal-muted portal-form-msg" role="status"></p>
      </div>
    </div>
  `
}

function signHref(doc) {
  const rawPath = String(doc.sign_path || '')
  const rawUrl = String(doc.sign_url || '')
  if (rawPath.startsWith('/firma/')) return rawPath
  if (rawUrl.includes('/firma/')) {
    try {
      const u = new URL(rawUrl, window.location.origin)
      return u.pathname + u.search
    } catch (_) {
      return rawUrl
    }
  }
  return '#'
}

function pendingRowHtml(doc) {
  const kind = escapeHtml(doc.document_kind_label || doc.document_kind || 'Documento')
  const title = escapeHtml(doc.title || 'Documento')
  const ref = doc.reference ? `<span class="portal-muted"> · ${escapeHtml(doc.reference)}</span>` : ''
  const href = escapeHtml(signHref(doc))
  return `
    <div class="pending-doc-row" style="display:flex;flex-wrap:wrap;gap:12px;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid rgba(148,163,184,.25);">
      <div>
        <strong>${title}</strong>${ref}
        <div class="portal-muted" style="font-size:.9rem;margin-top:2px;">${kind} · Pendiente de firma</div>
      </div>
      <a class="portal-btn portal-btn-primary" href="${href}">Firmar</a>
    </div>`
}

function signedRowHtml(doc) {
  const kind = escapeHtml(doc.document_kind_label || doc.document_kind || 'Documento')
  const title = escapeHtml(doc.title || 'Documento')
  const ref = doc.reference ? `<span class="portal-muted"> · ${escapeHtml(doc.reference)}</span>` : ''
  const label = escapeHtml(doc.status_label || 'Firmado')
  const signedAt = doc.signed_at
    ? `<span class="portal-muted"> · ${escapeHtml(new Date(doc.signed_at).toLocaleString('es-ES'))}</span>`
    : ''
  const viewPath = escapeHtml(doc.pdf_view_path || '')
  const dlPath = escapeHtml(doc.pdf_download_path || '')
  return `
    <div class="pending-doc-row" data-signed-row style="display:flex;flex-wrap:wrap;gap:12px;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid rgba(148,163,184,.25);">
      <div>
        <strong>${title}</strong>${ref}
        <div class="portal-muted" style="font-size:.9rem;margin-top:2px;">${kind} · ${label}${signedAt}</div>
      </div>
      <div style="display:flex;flex-wrap:wrap;gap:8px;">
        <button type="button" class="portal-btn portal-btn-rounded" data-pdf-view="${viewPath}">Ver PDF</button>
        <button type="button" class="portal-btn portal-btn-primary" data-pdf-dl="${dlPath}">Descargar</button>
      </div>
    </div>`
}

function sectionHtml(title, rowsHtml) {
  if (!rowsHtml) return ''
  return `
    <div style="padding:12px 16px 4px;font-weight:600;border-bottom:1px solid rgba(148,163,184,.15);">${escapeHtml(title)}</div>
    ${rowsHtml}`
}

export function bindPendingDocs(root, { navigate }) {
  if (!isClientLoggedIn()) {
    navigate('/cliente', { replace: true })
    return
  }

  if (!hasPermission('client_portal.signing.read') && !hasPermission('client_portal.docs.read')) {
    root.querySelector('#pending-msg').textContent = 'No tiene permiso para ver documentos de firma.'
    return
  }

  const session = getClientSession()
  let installationId = resolvePortalActiveInstallationId(session)
  const msg = root.querySelector('#pending-msg')
  const list = root.querySelector('#pending-list')
  const subtitle = root.querySelector('#pending-subtitle')
  const viewer = root.querySelector('#pending-pdf-viewer')
  const frame = root.querySelector('#pending-pdf-frame')
  const titleEl = root.querySelector('#pending-pdf-title')
  const dlLink = root.querySelector('#pending-pdf-dl-link')
  const zoomLabel = root.querySelector('[data-pdf-zoom-label]')
  let blobUrl = null
  let zoom = 1

  function revokeBlob() {
    if (blobUrl) {
      try {
        URL.revokeObjectURL(blobUrl)
      } catch (_) {}
      blobUrl = null
    }
  }

  function applyZoom() {
    if (!frame) return
    frame.style.transform = `scale(${zoom})`
    frame.style.width = `${100 / zoom}%`
    if (zoomLabel) zoomLabel.textContent = `${Math.round(zoom * 100)}%`
  }

  function closeViewer() {
    if (!viewer) return
    viewer.hidden = true
    viewer.classList.remove('is-open')
    if (frame) frame.removeAttribute('src')
    revokeBlob()
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {})
    }
  }

  function openViewer(blob, title, downloadName) {
    revokeBlob()
    blobUrl = URL.createObjectURL(blob)
    if (titleEl) titleEl.textContent = title || 'Documento PDF'
    if (frame) {
      frame.src = blobUrl
      zoom = 1
      applyZoom()
    }
    if (dlLink) {
      dlLink.href = blobUrl
      dlLink.download = downloadName || 'documento-firmado.pdf'
    }
    if (viewer) {
      viewer.hidden = false
      viewer.classList.add('is-open')
      try {
        viewer.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      } catch (_) {}
    }
  }

  root.querySelector('#pending-logout')?.addEventListener('click', async () => {
    closeViewer()
    try {
      await clientLogout()
    } catch (_) {}
    clearClientSession()
    navigate('/', { replace: true })
  })

  viewer?.querySelector('[data-pdf-close]')?.addEventListener('click', () => closeViewer())
  viewer?.querySelectorAll('[data-pdf-zoom]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const delta = Number(btn.getAttribute('data-pdf-zoom') || 0)
      zoom = Math.min(2.5, Math.max(0.5, Math.round((zoom + delta * 0.1) * 10) / 10))
      applyZoom()
    })
  })
  viewer?.querySelector('[data-pdf-fullscreen]')?.addEventListener('click', () => {
    const panel = viewer
    if (!panel) return
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {})
    } else {
      panel.requestFullscreen?.().catch(() => {})
    }
  })

  async function openPdf(path, download, title) {
    if (!path) return
    msg.textContent = download ? 'Preparando descarga…' : 'Abriendo PDF…'
    try {
      const blob = await fetchClientSigningPdf(path, installationId)
      if (download) {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'documento-firmado.pdf'
        a.click()
        URL.revokeObjectURL(url)
      } else {
        // ITM-DOC-PDF-VIEWER-SOT-01 — Ver = embeber en la app (no pestaña nueva)
        openViewer(blob, title, 'documento-firmado.pdf')
      }
      msg.textContent = ''
    } catch (e) {
      msg.textContent = e?.message || 'No se pudo obtener el PDF.'
    }
  }

  list?.addEventListener('click', (ev) => {
    const t = ev.target
    if (!(t instanceof HTMLElement)) return
    const view = t.closest('[data-pdf-view]')
    const dl = t.closest('[data-pdf-dl]')
    if (view instanceof HTMLElement && view.dataset.pdfView) {
      ev.preventDefault()
      const row = view.closest('[data-signed-row]')
      const title = row?.querySelector('strong')?.textContent || 'Documento PDF'
      openPdf(view.dataset.pdfView, false, title)
    }
    if (dl instanceof HTMLElement && dl.dataset.pdfDl) {
      ev.preventDefault()
      openPdf(dl.dataset.pdfDl, true)
    }
  })

  async function load() {
    if (!apiConfigured()) {
      msg.textContent = 'Servicio no disponible.'
      return
    }
    msg.textContent = 'Cargando…'
    closeViewer()
    try {
      const data = await clientPendingSignatures(installationId)
      const pending = data?.pending || []
      const signed = data?.signed || []
      persistPortalActiveInstallationId(installationId)

      const parts = []
      if (pending.length) {
        parts.push(`${pending.length} pendiente${pending.length === 1 ? '' : 's'}`)
      }
      if (signed.length) {
        parts.push(`${signed.length} firmado${signed.length === 1 ? '' : 's'}`)
      }
      subtitle.textContent = parts.length
        ? parts.join(' · ') + '.'
        : 'No hay contratos ni mandatos SEPA en esta sede.'

      if (!pending.length && !signed.length) {
        list.innerHTML = `<div style="padding:1.25rem;"><p class="portal-muted" style="margin:0;">Cuando AGH le envíe un contrato o mandato SEPA, aparecerá aquí. Tras firmarlo, seguirá disponible para ver y descargar.</p></div>`
      } else {
        list.innerHTML =
          sectionHtml('Pendientes de firma', pending.map(pendingRowHtml).join('')) +
          sectionHtml('Firmados', signed.map(signedRowHtml).join(''))
      }
      msg.textContent = ''
    } catch (e) {
      list.innerHTML = ''
      msg.textContent = e?.message || 'No se pudieron cargar los documentos.'
    }
  }

  load()
}

export { DOCS_PATH, LEGACY_PATH }
